import React, { useMemo } from 'react';
import { Hint } from '@/components/ui';
import { StudyFormHoverCard } from '@/components/data-import/StudyFormHoverCard';
import { buildFormTree, FormTreeNode, getParentFormIdFromNestedPath } from '../utils/formTreeUtils';
import { getFormDisplayName } from '@/utils/formUtils';
import type { FileToFormMapping, StudyForm } from '../types';
import { IconInfoCircle } from '@tabler/icons-react';

interface FormSelectorHintProps {
  selectedForm: StudyForm | null;
  availableForms: StudyForm[];
  mapping?: FileToFormMapping;
  useName?: boolean;
}

const getFormTypeLabel = (
  form: StudyForm,
  formTree: FormTreeNode[],
  nestedPath?: string,
  useName: boolean = false
): string => {
  const findInTree = (nodes: FormTreeNode[]): FormTreeNode | null => {
    for (const node of nodes) {
      if (node.id === form.id) return node;
      const found = findInTree(node.children);
      if (found) return found;
    }
    return null;
  };

  const treeNode = findInTree(formTree);
  if (!treeNode) {
    return `${form.fieldCount} fields`;
  }

  if (treeNode.depth === 0) {
    return treeNode.children.length > 0
      ? `Main Form • ${form.fieldCount} fields • ${treeNode.children.length} subform${treeNode.children.length > 1 ? 's' : ''}`
      : `Form • ${form.fieldCount} fields`;
  } else {
    // If nested_path exists, use it to get the correct parent
    let parent: FormTreeNode | null = null;
    if (nestedPath) {
      const parentFormId = getParentFormIdFromNestedPath(nestedPath, form.id);
      if (parentFormId !== null) {
        const findById = (nodes: FormTreeNode[]): FormTreeNode | null => {
          for (const node of nodes) {
            if (node.id === parentFormId) return node;
            const found = findById(node.children);
            if (found) return found;
          }
          return null;
        };
        parent = findById(formTree);
      }
    }
    
    // Fallback to finding first parent in tree
    if (!parent) {
      const findParent = (
        nodes: FormTreeNode[],
        targetId: number
      ): FormTreeNode | null => {
        for (const node of nodes) {
          if (node.children.some(child => child.id === targetId)) {
            return node;
          }
          const found = findParent(node.children, targetId);
          if (found) return found;
        }
        return null;
      };
      parent = findParent(formTree, form.id);
    }
    
    const parentName = parent
      ? useName
        ? parent.name
        : getFormDisplayName(parent)
      : 'Unknown parent';
    return `Subform of ${parentName} • ${form.fieldCount} fields`;
  }
};

export const FormSelectorHint: React.FC<FormSelectorHintProps> = ({
  selectedForm,
  availableForms,
  mapping,
  useName = false,
}) => {
  const formTree = useMemo(() => {
    return buildFormTree(availableForms);
  }, [availableForms]);

  // Convert availableForms array to Map for StudyFormHoverCard
  const formsMap = useMemo(() => {
    const map = new Map<number, StudyForm>();
    availableForms.forEach(form => {
      map.set(form.id, form);
    });
    return map;
  }, [availableForms]);

  if (!selectedForm) {
    return null;
  }

  const hintText = `${useName ? selectedForm.name : getFormDisplayName(selectedForm)} • ${getFormTypeLabel(selectedForm, formTree, mapping?.nested_path, useName)}`;

  return (
    <StudyFormHoverCard
      form={selectedForm}
      fieldCount={selectedForm.fieldCount}
      allForms={formsMap}
    >
      <Hint style={{ cursor: 'pointer' }} icon={<IconInfoCircle />}>
        {hintText}
      </Hint>
    </StudyFormHoverCard>
  );
};
